import { useEffect } from "react";

/**
 * Lightweight SEO component for an SPA. Sets the document title plus the
 * canonical, description, Open Graph, and Twitter meta tags imperatively so
 * crawlers that DO execute JS see correct per-page metadata, while the
 * static index.html keeps the marketing defaults for crawlers that don't.
 *
 * Pass an optional `jsonLd` payload to inject route-specific structured
 * data (e.g. Article, Product, BreadcrumbList).
 */
export interface SeoProps {
  title: string;
  description: string;
  /** Path or absolute URL. Defaults to current window.location.href. */
  canonical?: string;
  /** Absolute image URL for og:image / twitter:image. */
  image?: string;
  imageAlt?: string;
  /** og:type, defaults to "website". Use "article" for shareable user content. */
  ogType?: "website" | "article" | "profile";
  /** Set true on routes that should not be indexed (auth, gated app, etc.). */
  noindex?: boolean;
  /** Optional JSON-LD object or array of objects. Stringified into a script tag. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_URL = "https://meetglowie.com";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(payload: SeoProps["jsonLd"]) {
  const id = "seo-jsonld-route";
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!payload) return;
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.text = JSON.stringify(payload);
  document.head.appendChild(script);
}

export function Seo({
  title,
  description,
  canonical,
  image,
  imageAlt,
  ogType = "website",
  noindex = false,
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    document.title = title;

    const canonicalUrl = canonical
      ? canonical.startsWith("http")
        ? canonical
        : `${SITE_URL}${canonical.startsWith("/") ? canonical : `/${canonical}`}`
      : typeof window !== "undefined"
        ? window.location.href.split("#")[0]
        : SITE_URL;
    upsertLink("canonical", canonicalUrl);

    upsertMeta("name", "description", description);
    upsertMeta(
      "name",
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    );

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:type", ogType);
    const imageUrl = image || DEFAULT_IMAGE;
    upsertMeta("property", "og:image", imageUrl);
    if (imageAlt) upsertMeta("property", "og:image:alt", imageAlt);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", imageUrl);
    if (imageAlt) upsertMeta("name", "twitter:image:alt", imageAlt);

    setJsonLd(jsonLd);

    // No cleanup: leaving last-set values in place is harmless because
    // the next route mounting <Seo /> will overwrite them.
  }, [title, description, canonical, image, imageAlt, ogType, noindex, jsonLd]);

  return null;
}

export default Seo;